import _ from 'lodash';
import stable from 'stable';

import type { UserRole } from '@appTypes/esb';
import {
  programForUnitTypeId,
  programTypeNameForProgramTypeId,
  unitTypeIdForProgramTypeId,
} from '@modules/shared/constants';
import removeLeadingZeros from '@modules/utils/removeLeadingZeros';

import { key3Roles, organizationRoles } from './constants';
import {
  getCouncilRights,
  hasAdminAkelaRole,
  hasAdminRights,
  hasAdvancementsCoordinatorRights,
  hasApproverSBRole,
  hasCalendarEditorRights,
  hasCalendarPerms,
  hasCouncilRights,
  hasEditPaymentLogsPerms,
  hasEditUnitPaymentLogsPerms,
  hasEditYouthProfileRolesPerms,
  hasMBCRights,
  hasMeritBadgeCounselRights,
  hasParentGuardianRights,
  hasPermissionsManagerRights,
  hasScoutbookRole,
  hasSpecialLoginRole,
  hasSubCalendarPerms,
  hasSubUnitsRights,
  hasYouthMemberRights,
} from './permissionChecks';
import type { DedupedRoleType } from './types';

export function addIsScoutbookRole(
  personGuidRoles: UserRole[],
  userIdRoles: UserRole[] = [],
) {
  return [
    ...personGuidRoles.map(role => ({
      ...role,
      isScoutbookRole: !!role.sbRoleId,
    })),
    ...userIdRoles.map(uir => ({ ...uir, isScoutbookRole: true })),
  ];
}

/**
 * If any of the roles doesn't have a `programType/programTypeId/acceptGender` set, then it uses
 * the program found on the `unitProfiles`.
 */
export function enhanceWithUnitProfile(
  roles: DedupedRoleType[],
  unitProfiles: UserRole[],
) {
  // TODO: check if keyBy is the best, we might be getting incorrect programTypes
  const profilesByOrgId = _.keyBy(unitProfiles, 'organizationGuid');
  return roles.map(role => {
    const org = profilesByOrgId[role.organizationGuid];
    if (!org) return role;
    return {
      ...role,
      programType: role.programType || _.get(org, 'charter.programName'),
      programTypeId: role.programTypeId || _.get(org, 'charter.programId'),
      acceptGender: role.acceptGender || org.acceptGender,
    };
  });
}

/**
 *
 * Refactor(extracted): This seems to do the following:
 * - filter "valid" organizations
 * - Add isCalendarEditor flag
 * - "Compact" orgs
 */
export function filterValidOrgs(roles: DedupedRoleType[]) {
  const validOrganizations = roles.filter(
    org =>
      hasAdminRights(org) ||
      hasSpecialLoginRole(org) ||
      hasAdvancementsCoordinatorRights(org) ||
      hasCouncilRights(org) ||
      hasMeritBadgeCounselRights(org) ||
      hasYouthMemberRights(org) ||
      hasParentGuardianRights(org) ||
      hasCalendarEditorRights(org),
  );

  const validOrganizationsOrdered = stable(validOrganizations, org =>
    org.role === organizationRoles.ADVANCEMENT_COORDINATOR ? 1 : -1,
  );

  const compactOrganizationsSet = _.uniqWith(
    validOrganizationsOrdered,
    (organization, orgCompare) =>
      organization.organizationGuid === orgCompare.organizationGuid &&
      hasParentGuardianRights(organization) ===
        hasParentGuardianRights(orgCompare),
  );

  return compactOrganizationsSet.map(compactOrganizations => ({
    ...compactOrganizations,
    // TODO: move this to another function
    isCalendarEditor: validOrganizationsOrdered.some(
      ({ role, organizationGuid }) =>
        organizationRoles.CALENDAR_EDITOR.includes(role) &&
        compactOrganizations.organizationGuid === organizationGuid,
    ),
  }));
}

/**
 * This is the role that effectively gets stored with extra properties added
 * during initialization.
 */
export function normalizeUserRole(role: DedupedRoleType, personGuid: string) {
  const {
    organizationName,
    organizationNumber,
    programTypeId: roleProgramTypeId,
    programType,
    isAkelaAdvChair,
    ...rest
  } = role;
  const programTypeId =
    roleProgramTypeId ||
    programTypeNameForProgramTypeId[
      programType as keyof typeof programTypeNameForProgramTypeId
    ];
  const formattedOrganizationName = removeLeadingZeros(organizationName);
  const formattedOrganizationNumber = removeLeadingZeros(organizationNumber);
  const unitTypeId =
    unitTypeIdForProgramTypeId[
      +programTypeId as keyof typeof unitTypeIdForProgramTypeId
    ];
  const isKey3 = role.roleTypes.some(({ roleType }) =>
    key3Roles.includes(roleType),
  );
  const { isCouncil, hasCouncilEditRights } = getCouncilRights(role);
  const isMeritBadgeCounselor = hasMeritBadgeCounselRights(role);
  const isYouthMember = hasYouthMemberRights(role);
  const isParentGuardian = hasParentGuardianRights(role);
  const isAdminAkelaRole = hasAdminAkelaRole(role);
  const isScoutbookRole =
    !isAdminAkelaRole &&
    !isAkelaAdvChair &&
    (hasScoutbookRole(role) || hasAdvancementsCoordinatorRights(role));

  const canAproveAdvancements =
    isScoutbookRole &&
    (hasApproverSBRole(role) || hasAdvancementsCoordinatorRights(role));
  const canEditSubUnits = hasSubUnitsRights(role);
  const canSeeMBCs = hasMBCRights(role);
  const canEditCalendar = hasCalendarPerms(role) || role.isCalendarEditor;
  // NOTE: canEditSubCalendar is being used for logic in calendar and roster
  // it's used for users that has limited access to only their subunits
  const canEditSubCalendar = hasSubCalendarPerms(role);
  const canEditYouthProfilePerms = hasEditYouthProfileRolesPerms(role);

  const isCouncilAdmin = hasCouncilRights(role);
  const hasLogInRole = hasSpecialLoginRole(role);
  const canEditPaymentLogs = hasEditPaymentLogsPerms(role);
  const canEditUnitPaymentLogs = hasEditUnitPaymentLogsPerms(role);
  const canEditPermissionsManager = hasPermissionsManagerRights(role);

  return {
    ...rest,
    programType,
    unitTypeId,
    programTypeId,
    isKey3,
    isCouncil,
    isCouncilAdmin,
    hasCouncilEditRights,
    isMeritBadgeCounselor,
    isYouthMember,
    isParentGuardian,
    isScoutbookRole,
    canAproveAdvancements,
    canEditSubUnits,
    canSeeMBCs,
    canEditCalendar,
    canEditSubCalendar,
    hasLogInRole,
    canEditPaymentLogs,
    canEditUnitPaymentLogs,
    canEditPermissionsManager,
    canEditYouthProfilePerms,
    personGuid: isYouthMember && personGuid,
    program: programForUnitTypeId[unitTypeId],
    organizationName: formattedOrganizationName,
    organizationNumber: formattedOrganizationNumber,
  };
}
